import { User } from '@/types/user';
import { License } from '@/types/license';
import { RemoteData } from '@/lib/frontend/store/helpers/remoteData';
import { AppState } from '@/lib/frontend/store/reducers';
import { createSelector } from '@reduxjs/toolkit';

const selectUsersByID = (state: AppState) => state.usersByID;
export const selectSession = (state: AppState) => state.session;
export const selectRecaptcha = (state: AppState) => state.recaptcha;
export const selectSignInPage = (state: AppState) => state.pages.signInPage;
const selectAccountsByID = (state: AppState) => state.accountsByID;
export const selectCart = (state: AppState) => state.shop.cart;
export const selectCatalog = (state: AppState) => state.shop.catalog;
export const selectRentalStats = (state: AppState) => state.shop.rentalStats;
export const selectCheckoutPage = (state: AppState) => state.pages.checkoutPage;
export const selectSearch = (state: AppState) => state.search;
export const selectStoreCreditChangesByAccountID = (state: AppState) =>
  state.storeCreditChangesByAccountID;
export const selectCurrentUser = (state: AppState) => state.currentUser;
export const selectLicensesBySerial = (state: AppState) => state.licensesBySerial;
export const selectLicenseTradeInValueBySerial = (state: AppState) =>
  state.licenseTradeInValueBySerial;
export const selectOrdersByID = (state: AppState) => state.ordersByID;
export const selectOrdersByNumber = (state: AppState) => state.ordersByNumber;
export const selectSeatColumnToggles = (state: AppState) =>
  state.pages.licensePage?.seatTableColumnToggles;
export const selectNotifications = (state: AppState) => state.notifications;
export const selectInvoicesByNumber = (state: AppState) => state.invoicesByNumber;
export const selectInvoicePage = (state: AppState) => state.pages.invoicePage;
export const selectConfirmEmailAddressChangePage = (state: AppState) =>
  state.pages.confirmEmailAddressChangePage;
export const selectConfirmNewEmailAddressPage = (state: AppState) =>
  state.pages.confirmNewEmailAddressPage;
export const selectUpgradeToV5Page = (state: AppState) => state.pages.upgradeToV5Page;
export const selectCartPage = (state: AppState) => state.pages.cartPage;

export const selectShop = (state: AppState) => state.shop;

export const selectProducts = (state: AppState) => state.shop.products;

export const selectModal = (state: AppState) => state.modal;

export const selectSessionAccount = createSelector(
  [selectAccountsByID, selectSession],
  (accountsByID, session) => {
    const sessionAccountID = session.accountID.value;
    const accountRemoteData = sessionAccountID != null ? accountsByID[sessionAccountID] : null;
    return accountRemoteData && accountRemoteData.value;
  },
);

export const selectSessionUser = createSelector(
  [selectUsersByID, selectSession],
  (usersByID, session) => {
    const sessionUserID = session.userID.value;
    const userRemoteData = sessionUserID != null ? usersByID[sessionUserID] : null;
    return userRemoteData && userRemoteData.value;
  },
);

export const selectAuthenticatingUser = createSelector(
  [selectUsersByID, selectSession],
  (usersByID, session) => {
    const authenticatingUserID = session.authenticatingUserID.value;
    const remoteData = authenticatingUserID != null ? usersByID[authenticatingUserID] : null;
    return remoteData && remoteData.value;
  },
);

export const selectUserLicenses = createSelector(
  [selectCurrentUser, selectLicensesBySerial],
  (currentUser, licensesBySerial) => {
    const currentUserLicenseSerials = currentUser.licenses;
    const licenses: Array<RemoteData<License>> =
      currentUserLicenseSerials.value != null
        ? currentUserLicenseSerials.value.map((licenseSerial) => {
            return licensesBySerial[licenseSerial];
          })
        : [];

    return licenses;
  },
);

// unwraps the session user remote data, returns null if anything is uninitialized or pending
function getSessionUser(appState: AppState): User | undefined | null {
  const sessionUserID = appState.session.userID.value;
  const userRemoteData = sessionUserID != null ? appState.usersByID[sessionUserID] : null;
  return userRemoteData && userRemoteData.value;
}

// unwraps the session sign in user remote data
function getAuthenticatingUser(appState: AppState): User | undefined | null {
  const authenticatingUserID = appState.session.authenticatingUserID.value;
  const remoteData = authenticatingUserID != null ? appState.usersByID[authenticatingUserID] : null;
  return remoteData && remoteData.value;
}

export { getSessionUser, getAuthenticatingUser };
